import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PhotoCamera as PhotoCameraIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

/**
 * UserSettings component with SafeLine theme matching verification pages
 */
const UserSettings = () => {
  const { currentUser } = useAuth();
  
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };
  
  const [profile, setProfile] = useState({
    firstName: currentUser?.firstName || 'John',
    lastName: currentUser?.lastName || 'Doe',
    email: currentUser?.email || 'john.doe@safelineacademy.edu',
    phone: currentUser?.phone || '+1 (555) 123-4567',
    role: currentUser?.role || 'Administrator',
    department: currentUser?.department || 'Administration',
    title: currentUser?.title || 'School Administrator',
    bio: currentUser?.bio || 'Experienced school administrator with a focus on student safety and security.',
    avatar: currentUser?.avatar || null
  });

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    weeklyReports: true,
    monthlyReports: false,
    securityAlerts: true,
    systemUpdates: false,
    darkMode: false,
    compactView: false,
    autoSave: true
  });

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleProfileChange = (key, value) => {
    setProfile(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    
    setTimeout(() => {
      setLoading(false);
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
    }, 1000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleProfileChange('avatar', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = () => {
    setProfile({
      firstName: currentUser?.firstName || 'John',
      lastName: currentUser?.lastName || 'Doe',
      email: currentUser?.email || 'john.doe@safelineacademy.edu',
      phone: currentUser?.phone || '+1 (555) 123-4567',
      role: currentUser?.role || 'Administrator',
      department: currentUser?.department || 'Administration',
      title: currentUser?.title || 'School Administrator',
      bio: currentUser?.bio || 'Experienced school administrator with a focus on student safety and security.',
      avatar: currentUser?.avatar || null
    });
    setSnackbar({
      open: true,
      message: 'Profile reset to original values',
      severity: 'info'
    });
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const settingsSteps = [
    'Profile Information',
    'Account Preferences',
    'Security Settings'
  ];

  return (
    <Box sx={{}}>
      <Grid container spacing={2}>
        {/* Settings Process Guide */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ color: safeLineColors.flameOrange, mr: 1.5 }} />
                  <Typography variant="h6" sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'bold' }}>
                    User Settings Guide
                  </Typography>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            <CardContent>
              <Stepper orientation="vertical">
                {settingsSteps.map((step, index) => (
                  <Step key={step} active={true}>
                    <StepLabel>
                      <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                        {step}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                        {index === 0 && 'Update your personal information and profile details'}
                        {index === 1 && 'Configure notification and display preferences'}
                        {index === 2 && 'Manage password and security settings'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Form */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2,
                      backgroundColor: safeLineColors.flameOrange,
                      mr: 2
                    }}>
                      <PersonIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: safeLineColors.darkCharcoal 
                      }}>
                        User Settings
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal 
                      }}>
                        Manage your profile, preferences, and security settings
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            
            <CardContent sx={{ p: 3 }}>
              {/* Profile Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <PersonIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Profile Information
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ position: 'relative', mr: 3 }}>
                    <Avatar
                      src={profile.avatar}
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: safeLineColors.flameOrange,
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {getInitials(profile.firstName, profile.lastName)}
                    </Avatar>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: safeLineColors.flameOrange,
                        color: safeLineColors.pureWhite,
                        '&:hover': {
                          bgcolor: safeLineColors.brightOrange,
                        },
                        width: 32,
                        height: 32
                      }}
                      component="label"
                    >
                      <PhotoCameraIcon sx={{ fontSize: 16 }} />
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handleAvatarUpload}
                      />
                    </IconButton>
                  </Box>
                  
                  <Box>
                    <Typography variant="h5" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'bold'
                    }}>
                      {profile.firstName} {profile.lastName}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: safeLineColors.mediumCharcoal 
                    }}>
                      {profile.title} • {profile.department}
                    </Typography>
                    <Chip
                      label={profile.role}
                      size="small"
                      sx={{
                        backgroundColor: safeLineColors.lightEmerald,
                        color: safeLineColors.forestGreen,
                        border: `1px solid ${safeLineColors.emerald}`,
                        fontWeight: 'medium',
                        mt: 1
                      }}
                    />
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={profile.firstName}
                      onChange={(e) => handleProfileChange('firstName', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={profile.lastName}
                      onChange={(e) => handleProfileChange('lastName', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={profile.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={profile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Bio"
                      value={profile.bio}
                      onChange={(e) => handleProfileChange('bio', e.target.value)}
                      variant="outlined"
                      multiline
                      rows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Account Preferences */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <SettingsIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Account Preferences
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Notifications
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preferences.emailNotifications}
                            onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Email Notifications
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preferences.smsNotifications}
                            onChange={(e) => handlePreferenceChange('smsNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            SMS Notifications
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preferences.pushNotifications}
                            onChange={(e) => handlePreferenceChange('pushNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Push Notifications
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Display & Reports
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preferences.darkMode}
                            onChange={(e) => handlePreferenceChange('darkMode', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Dark Mode
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preferences.weeklyReports}
                            onChange={(e) => handlePreferenceChange('weeklyReports', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Weekly Reports
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={preferences.securityAlerts}
                            onChange={(e) => handlePreferenceChange('securityAlerts', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Security Alerts
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Security Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <SecurityIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Security Settings
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwords.current}
                      onChange={(e) => handlePasswordChange('current', e.target.value)}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('current')}
                              edge="end"
                            >
                              {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwords.new}
                      onChange={(e) => handlePasswordChange('new', e.target.value)}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('new')}
                              edge="end"
                            >
                              {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwords.confirm}
                      onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility('confirm')}
                              edge="end"
                            >
                              {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  sx={{
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      backgroundColor: safeLineColors.orangeTint,
                      borderColor: safeLineColors.brightOrange
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProfile}
                  disabled={loading}
                  sx={{
                    background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSettings; 